import { FC, PropsWithChildren, useCallback, useEffect, useState } from "react";

import { Variant } from "@amplitude/experiment-js-client";
import { AmplitudeVariantValue } from "@simplyk/common";

import { AMPLITUDE_EXPERIMENT_API_KEY } from "../constants/amplitude";
import { AmplitudeContext, GetExperimentValueProps } from "../contexts/AmplitudeContext";
import { useCurrentUserContext } from "../contexts/CurrentUserContext";

import { isTest } from "@/common/constants/env";

interface AmplitudeFlags {
  [key: string]: AmplitudeVariantValue;
}

const fetchAmplitudeFlags = async (userId?: string, organizationId?: string): Promise<AmplitudeFlags> => {
  if (!AMPLITUDE_EXPERIMENT_API_KEY) {
    return {};
  }

  const params = new URLSearchParams();
  if (userId) {
    params.append("user_id", userId);
  }
  if (organizationId) {
    params.append("context", JSON.stringify({ groups: { organization: [organizationId] } }));
  }

  const response = await fetch(`https://api.lab.amplitude.com/v1/vardata?${params.toString()}`, {
    method: "GET",
    headers: { Authorization: `Api-Key ${AMPLITUDE_EXPERIMENT_API_KEY}` },
  });

  if (!response.ok) {
    return {};
  }

  const data: JSON = (await response.json()) as JSON;
  const flags: AmplitudeFlags = {};

  Object.entries(data).forEach(([key, variant]: [string, unknown]) => {
    if (variant && typeof variant === "object" && "key" in variant) {
      flags[key] = (variant as { key: string }).key as AmplitudeVariantValue;
    }
  });

  return flags;
};

export const LightAmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isAmplitudeLoading, setIsAmplitudeLoading] = useState(!isTest);
  const [flags, setFlags] = useState<AmplitudeFlags>({});
  const { organization, currentUser } = useCurrentUserContext();

  useEffect(() => {
    if (isTest) {
      return;
    }

    const loadFlags = async () => {
      const fetchedFlags = await fetchAmplitudeFlags(currentUser?.id, organization?.id);
      setFlags(fetchedFlags);
      setIsAmplitudeLoading(false);
    };

    loadFlags();
  }, [currentUser?.id, organization?.id]);

  const getExperimentValue = useCallback(
    async ({ flagKey }: GetExperimentValueProps): Promise<Variant | undefined> => {
      if (isTest) {
        return { value: AmplitudeVariantValue.OFF } as Variant;
      }
      const value = flags[flagKey] ?? AmplitudeVariantValue.OFF;

      return { value } as Variant;
    },
    [flags]
  );

  const logAmplitudeEvent = useCallback((event: string, payload?: { [key: string]: unknown }) => {
    // eslint-disable-next-line no-console
    console.log(`📊 Amplitude: Log Event '${event}'`, payload);
  }, []);

  const resetAmplitude = useCallback(() => {
    if (isTest) {
      return;
    }
    setFlags({});
  }, []);

  return (
    <AmplitudeContext.Provider
      value={{
        isAmplitudeLoading,
        getExperimentValue,
        logAmplitudeEvent,
        resetAmplitude,
        getAmplitudeDeviceId: () => undefined,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};
